import React, { useEffect } from "react";
import { Mail, MapPin, MessageCircle, Calendar } from "lucide-react";
import { Helmet } from "react-helmet-async";
import LeadForm from "../components/LeadForm";
import { SITE, WHATSAPP_LINK } from "../data/site";
import { useLocation } from "react-router-dom";

export default function Contact() {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash === "#lead-form") {
      const el = document.getElementById("lead-form");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [hash]);

  return (
    <>
    <Helmet>
      <title>Contact Us | Indus Art — Request a Quote or Book a Discovery Call</title>
      <meta name="description" content="Reach our export desk directly. Request a wholesale quote, ask about custom orders, or book a 20-minute discovery call with an Indus Art specialist." />
      <meta property="og:title" content="Contact Indus Art | Request a Quote" />
      <meta property="og:description" content="Get a personalised quote within one business day. Hand block printed textiles from Jaipur — wholesale &amp; export." />
      <meta property="og:url" content="https://www.indusartjaipur.com/contact" />
      <link rel="canonical" href="https://www.indusartjaipur.com/contact" />
    </Helmet>
    <main data-testid="contact-page" className="pt-28">
      <section className="py-16 md:py-24 bg-[#faf6ef]">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 text-center">
          <div className="text-[#b4462a] text-xs tracking-[0.3em] uppercase mb-4">Contact</div>
          <h1 className="font-serif text-5xl md:text-7xl leading-[1.05] tracking-tight font-light">Let's talk fabric.</h1>
          <p className="mt-6 text-lg text-[#1a1a1a]/70 max-w-2xl mx-auto leading-[1.8]">A short form below reaches our export desk directly. Prefer voice? Chat? Calendar? All here.</p>
        </div>
      </section>

      <section className="py-16 bg-white border-y border-[#1a1a1a]/10">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-14 grid md:grid-cols-3 gap-px bg-[#1a1a1a]/10 border border-[#1a1a1a]/10">
          <InfoCard icon={MapPin}        title="Visit"     body={SITE.address}      note="Jaipur, India · IST" />
          <InfoCard icon={Mail}          title="Write"     body={SITE.email}        href={`mailto:${SITE.email}`} note={SITE.responseTime} />
          <InfoCard icon={MessageCircle} title="WhatsApp"  body="Chat with our export desk" href={WHATSAPP_LINK()} note="Mon–Sat · 10:00–18:30 IST" />
        </div>
      </section>

      <section id="lead-form" className="py-24 bg-[#faf6ef]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-14 grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5">
            <div className="text-[#b4462a] text-xs tracking-[0.3em] uppercase mb-4">Request a Quote</div>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.1] font-light mb-6">A personalised quote, within one business day.</h2>
            <p className="text-[#1a1a1a]/75 leading-[1.8] mb-8">Tell us about category, MOQ, and design path — we'll come back with pricing, lead time, and a sampling plan.</p>

            <div className="border-t border-[#1a1a1a]/15 pt-6">
              <div className="flex items-center gap-3 mb-4">
                <Calendar size={18} strokeWidth={1.5} className="text-[#1e2a5a]" />
                <div className="font-serif text-lg text-[#1e2a5a]">Prefer a 20-minute call?</div>
              </div>
              <a href={SITE.calendlyUrl} target="_blank" rel="noreferrer" data-testid="calendly-link" className="editorial-link text-sm tracking-[0.2em] uppercase text-[#b4462a]">Book a discovery call →</a>
            </div>
          </div>
          <div className="md:col-span-7">
            <LeadForm source="contact_page" />
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="aspect-[16/7] w-full overflow-hidden border border-[#1a1a1a]/10 rounded-3xl">
            <iframe
              title="Indus Art Jaipur location"
              src="https://www.openstreetmap.org/export/embed.html?bbox=75.78%2C26.84%2C75.83%2C26.87&layer=mapnik&marker=26.8546%2C75.8067"
              className="w-full h-full border-0"
              loading="lazy"
            />
          </div>
        </div>
      </section>
    </main>
    </>
  );
}

function InfoCard({ icon: Icon, title, body, href, note }) {
  const Comp = href ? "a" : "div";
  return (
    <Comp href={href} target={href?.startsWith("http") ? "_blank" : undefined} rel="noreferrer" data-testid={`info-${title.toLowerCase()}`} className="bg-white p-8 md:p-10 block hover:bg-[#faf6ef] transition">
      <Icon size={24} strokeWidth={1.25} className="text-[#b4462a] mb-5" />
      <div className="text-xs tracking-[0.25em] uppercase text-[#8a9a7b] mb-2">{title}</div>
      <div className="font-serif text-xl text-[#1e2a5a] mb-3 whitespace-pre-line">{body}</div>
      <div className="text-xs text-[#1a1a1a]/60">{note}</div>
    </Comp>
  );
}
